import type { IApiKeyScopeGroup } from "../types/api-key";
import { EApiKeyScope } from "../types/api-key";

export const API_KEY_NAME_MAX_LENGTH = 50;
export const API_KEYS_QUERY_KEY = "api-keys";

// NOTE: Query key for the user-scoped listing (`GET /api-keys/user`), which
// returns every key the user owns across all of their projects. Shared so web
// and mobile invalidate the same cache entry after a create or delete.
export const USER_API_KEYS_QUERY_KEY = "userApiKeys";

export const API_KEY_LOGS_QUERY_KEY = "api-key-logs";

export const API_KEY_LOGS_DEFAULT_LIMIT = 50;
export const API_KEY_LOGS_DEFAULT_OFFSET = 0;

export const API_KEY_EXPIRATION_PRESET_OPTIONS = [
  { value: "7d", days: 7 },
  { value: "30d", days: 30 },
  { value: "60d", days: 60 },
  { value: "90d", days: 90 },
  { value: "custom", days: null },
  { value: "none", days: null },
] as const;

export const API_KEY_EXPIRATION_DATE_FORMAT = "yyyy-MM-dd";

export const MASKED_API_KEY_CHARACTERS = "*";
export const DEFAULT_EXPIRATION_PRESET = "7d";

// NOTE: NSAI-6881 — every scope in display order, and the same scopes grouped
// by the resource they open, which is how both clients lay out the picker.
export const API_KEY_SCOPES: readonly EApiKeyScope[] =
  Object.values(EApiKeyScope);

export const API_KEY_SCOPE_GROUPS: readonly IApiKeyScopeGroup[] = [
  {
    id: "chat",
    scopes: [
      EApiKeyScope.CHAT,
      EApiKeyScope.IMAGES,
      EApiKeyScope.MODELS,
      EApiKeyScope.FILES,
    ],
  },
  {
    id: "assistants",
    scopes: [EApiKeyScope.ASSISTANTS_READ, EApiKeyScope.ASSISTANTS_WRITE],
  },
  { id: "skills", scopes: [EApiKeyScope.SKILLS_READ] },
  {
    id: "prompts",
    scopes: [EApiKeyScope.PROMPTS_READ, EApiKeyScope.PROMPTS_WRITE],
  },
  {
    id: "agents",
    scopes: [EApiKeyScope.AGENTS_READ, EApiKeyScope.AGENTS_RUN],
  },
  {
    id: "workflows",
    scopes: [
      EApiKeyScope.WORKFLOWS_READ,
      EApiKeyScope.WORKFLOWS_WRITE,
      EApiKeyScope.WORKFLOWS_RUN,
    ],
  },
];

// NOTE: NSAI-6881 — i18n key segment per scope under `apiKeys.scopes.items`.
// A scope value cannot be the key itself: `:` is i18next's namespace separator.
export const API_KEY_SCOPE_I18N_KEYS = {
  [EApiKeyScope.CHAT]: "chat",
  [EApiKeyScope.IMAGES]: "images",
  [EApiKeyScope.MODELS]: "models",
  [EApiKeyScope.FILES]: "files",
  [EApiKeyScope.ASSISTANTS_READ]: "assistantsRead",
  [EApiKeyScope.ASSISTANTS_WRITE]: "assistantsWrite",
  [EApiKeyScope.SKILLS_READ]: "skillsRead",
  [EApiKeyScope.PROMPTS_READ]: "promptsRead",
  [EApiKeyScope.PROMPTS_WRITE]: "promptsWrite",
  [EApiKeyScope.AGENTS_READ]: "agentsRead",
  [EApiKeyScope.AGENTS_RUN]: "agentsRun",
  [EApiKeyScope.WORKFLOWS_READ]: "workflowsRead",
  [EApiKeyScope.WORKFLOWS_WRITE]: "workflowsWrite",
  [EApiKeyScope.WORKFLOWS_RUN]: "workflowsRun",
} as const satisfies Record<EApiKeyScope, string>;
